import { Badge, IconButton, Button as MuiButton } from "@mui/material";
import React, { Component } from "react";
import { NavLink } from "react-router-dom";
import Theme from "../../common/Theme";
import Tooltip from "../Tooltip";

interface ButtonProps {
  hasBorder?: boolean;
  hasFocusBorder?: boolean;
  background?: string | null;
  label?: string;
  size?: "sm" | "md" | "lg";
  theme?: any; // You may want to type this properly based on the theme type you are using
  variant?: "outlined" | "filled";
  path?: string;
  href?: string;
  onClick?: () => void;
  autoFocus?: boolean;
  icon?: React.ReactNode;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  sx?: Record<string, any>;
  className?: string | null;
  badgeProps?: Record<string, any>;
  color?: string;
  hoverColor?: string;
  activeColor?: string;
  padding?: Record<string, string>;
  labelColor?: string;
  target?: string | null;
  filledLabelColor?: string;
  showLabelFromBreakpoint?: string;
  tooltip?: string;
  toolTipEnterDelay?: number;
  disabled?: boolean;
  type?: "submit" | "button" | "reset";
  form?: string;
}

interface ButtonState {
  disabled: boolean;
}

export default class Button extends Component<ButtonProps, ButtonState> {
  color = Theme.getStyleVar("--white-wh-900");
  hoverColor = Theme.getStyleVar("--neon-blue-nb-050");
  activeColor = Theme.getStyleVar("--neon-blue-nb-100");
  filledColor = Theme.getStyleVar("--neon-blue-nb-500");
  filledHoverColor = Theme.getStyleVar("--neon-blue-nb-700");
  filledActiveColor = Theme.getStyleVar("--neon-blue-nb-900");
  iconColor = Theme.getStyleVar("--neon-blue-nb-500");
  filledIconColor = Theme.getStyleVar("--white-wh-900");
  static padding = { sm: "16px 12px", md: "10px 16px", lg: "12px 24px" };
  static iconPadding = { sm: "8px", md: "8px", lg: "12px" };
  static iconSize = { sm: "20px", md: "20px", lg: "24px" };
  static fontSize = { sm: "14px", md: "14px", lg: "16px" };
  static size = { sm: "32px", md: "40px", lg: "40px" };
  static borderRadius = { sm: "16px", md: "20px", lg: "24px" };
  static focusBorderRadius = { sm: "20px", md: "24px", lg: "28px" };
  static focusOffset = "-4px";
  static outlinedfocusOffset = "-5px";

  static defaultProps: Partial<ButtonProps> = {
    size: "lg",
    variant: "outlined",
    filledLabelColor: "var(--white-wh-900)",
    target: null,
    hasFocusBorder: true,
    background: null,
  };

  constructor(props: ButtonProps) {
    super(props);

    this.state = {
      disabled: props.disabled ? true : false,
    };
  }

  componentDidUpdate(prevProps: ButtonProps) {
    if (prevProps.disabled !== this.props.disabled) {
      this.setState({ disabled: this.props.disabled || false });
    }
  }

  setEnabled(enabled: boolean) {
    this.setState({ disabled: !enabled });
  }

  renderButton() {
    const {
      hasBorder,
      hasFocusBorder,
      background,
      label,
      size = "lg",
      theme,
      variant,
      path,
      href,
      onClick,
      autoFocus,
      icon,
      startIcon,
      endIcon,
      sx,
      className,
      badgeProps,
      color,
      hoverColor,
      activeColor,
      labelColor,
      target,
      filledLabelColor,
      showLabelFromBreakpoint,
      type,
      form,
    } = this.props;
    const { disabled } = this.state;

    const btnClass =
      "klayo-button" +
      (icon ? " klayo-iconbutton" : "") +
      (className ? " " + className : "") +
      (size ? " klayo-button-" + size : "") +
      (variant && !icon ? " klayo-button-" + variant : "");

    const isOutlined = variant === "outlined";
    const isFilled = variant === "filled";

    // Default label color per variant (text is a blue link-style action); an explicit
    // labelColor prop always wins. Uses CSS var() so the value resolves at paint time —
    // avoids the dev/prod discrepancy from reading CSS vars via getComputedStyle at module load.
    const resolvedLabelColor =
      labelColor ?? (variant === "text" ? "var(--neon-blue-nb-500)" : "var(--grey-gr-900)");

    const style: Record<string, any> = {
      border: hasBorder
        ? !disabled
          ? "1px solid var(--neon-blue-nb-100)!important"
          : "1px solid var(--grey-gr-300)!important"
        : null,
      background: background || null,
      padding: icon ? Button.iconPadding[size] : Button.padding[size],
      backgroundColor:
        color || (isFilled ? this.filledColor : icon ? null : this.color),
      "&:hover": {
        backgroundColor:
          hoverColor || (isFilled ? this.filledHoverColor : this.hoverColor),
      },
      "&:active": {
        backgroundColor:
          activeColor || (isFilled ? this.filledActiveColor : this.activeColor),
      },
      "&:focus::before": hasFocusBorder
        ? {
            border: "2px solid var(--neon-blue-nb-400)",
            borderRadius: Button.focusBorderRadius[size],
            top: isOutlined ? Button.outlinedfocusOffset : Button.focusOffset,
            right: isOutlined ? Button.outlinedfocusOffset : Button.focusOffset,
            bottom: isOutlined
              ? Button.outlinedfocusOffset
              : Button.focusOffset,
            left: isOutlined ? Button.outlinedfocusOffset : Button.focusOffset,
          }
        : {},
      ["& .MuiButton-startIcon, & .MuiButton-endIcon"]: {
        width: Button.iconSize[size],
      },
      ["& .klayo-button_label"]: {
        color: isFilled ? filledLabelColor : resolvedLabelColor,
        fontSize: Button.fontSize[size],
        fontWeight: isFilled ? "400" : "600",
      },
      ['& .MuiButton-startIcon svg path[fill="#111827"], & .MuiButton-startIcon svg path[fill="#727DF9"], & .MuiButton-endIcon svg path[fill="#111827"], & .MuiButton-endIcon svg path[fill="#727DF9"], & .MuiButton-endIcon svg path[fill="#1B3DF2"]']:
        {
          fill: isFilled ? this.filledIconColor : this.iconColor,
        },
      ...sx,
    };

    if (theme && showLabelFromBreakpoint) {
      style[theme.breakpoints.down(showLabelFromBreakpoint)] = {
        padding: "0!important",
        minWidth: Button.size[size],
      };

      style["& .klayo-button_label"] = {
        [theme.breakpoints.down(showLabelFromBreakpoint)]: {
          display: "none",
        },
        [theme.breakpoints.up(showLabelFromBreakpoint)]: {
          display: "block",
        },
      };

      style["& .MuiButton-startIcon, & .MuiButton-endIcon"][
        theme.breakpoints.down(showLabelFromBreakpoint)
      ] = {
        margin: "0",
      };
    }

    return icon ? (
      <IconButton<any>
        className={btnClass}
        onClick={onClick}
        disabled={disabled}
        disableRipple={true}
        to={path}
        component={path ? NavLink : null}
        sx={style}
      >
        {badgeProps && badgeProps.show !== false ? (
          <Badge {...badgeProps}>{icon}</Badge>
        ) : (
          icon
        )}
      </IconButton>
    ) : (
      <MuiButton<any>
        className={btnClass}
        href={href}
        target={target}
        to={path}
        component={path ? NavLink : href ? "a" : null}
        startIcon={startIcon}
        endIcon={endIcon}
        onClick={onClick}
        disableRipple={true}
        disabled={disabled}
        autoFocus={autoFocus}
        sx={style}
        type={type}
        form={form}
      >
        <div className="klayo-button_label">{label}</div>
      </MuiButton>
    );
  }

  render() {
    const { tooltip, toolTipEnterDelay } = this.props;

    return tooltip ? (
      <Tooltip title={tooltip} enterDelay={toolTipEnterDelay}>
        {this.renderButton()}
      </Tooltip>
    ) : (
      this.renderButton()
    );
  }
}
